import { Route, Routes } from 'react-router';

import { useEffect } from 'react';

import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

import {
  RouterContext,
  AppraisalLimitProtectedRoute,
  SimulationLimitProtectedRoute,
  UserAuthRoutes,
} from '@app-velocity/modules/users';
import { ProtectedRoute } from '@app-velocity/request-gateway';

import { FlagProtectedRoute } from '@app-velocity/config';

import { AuthRoutesPaths, useUserData } from '@app-velocity/libs/auth';

import { Home, UpgradeNeeded } from './pages';
import { AuthLayout } from './layouts/AuthLayout/AuthLayout';
import { Details } from './pages/Prototypes/Scenarios/Details';
import { WorkspaceProtectedRoute } from './routes/WorkspaceProtectedRoute';
import { AuthenticationPage } from './pages/AuthenticationPage';
import { RouteWithVersion } from './routes/RouteWithVersion';
import { SetUpHumanTesting } from './pages/Prototypes/Scenarios/SetUpHumanTesting';
import { Workspace } from './pages/Workspace';
import { SelectPageForReview } from './pages/Reviews/SelectPageForReview';
import { Guest } from './pages/Guest';
import { NewAuthReview } from './pages/Reviews/NewAuthReview';
import { NewGuestReview } from './pages/Reviews/NewGuestReview/NewGuestReview';
import { SelectFlowGuest } from './pages/Prototypes/Scenarios/New/SelectFlow/SelectFlowGuest';
import { SelectFlowAuth } from './pages/Prototypes/Scenarios/New/SelectFlow/SelectFlowAuth';
import { ConfigureScenarioAuth } from './pages/Prototypes/Scenarios/New/ConfigureScenario/ConfigureScenarioAuth';
import { ConfigureScenarioGuest } from './pages/Prototypes/Scenarios/New/ConfigureScenario/ConfigureScenarioGuest';
import { SimulationAuth } from './pages/Prototypes/Scenarios/Simulation/SimulationAuth';
import { SimulationGuest } from './pages/Prototypes/Scenarios/Simulation/SimulationGuest';
import { GuestLayout } from './layouts/GuestLayout';
import { GuestReviewDetails } from './pages/Reviews/GuestReviewDetails';
import { AuthReviewDetails } from './pages/Reviews/AuthReviewDetails';

// Alias for Route component to avoid confusion with regular route
const LayoutRoute = Route;

export const AppRouting = () => {
  const { userEmail, userId } = useUserData();
  useEffect(() => {
    if (!userEmail || !userId) {
      Sentry.setUser(null);

      return;
    }

    Sentry.setUser({
      email: userEmail,
      id: userId,
    });

    if (posthog._isIdentified()) return;

    posthog.identify(userEmail, {
      userId: userId,
    });
  }, [userEmail, userId]);

  return (
    <RouterContext.Provider value={{ strategy: 'FIGMA_PLUGIN' }}>
      <Routes>
        <LayoutRoute
          element={<ProtectedRoute redirectPath={AuthRoutesPaths.Guest} />}
        >
          <LayoutRoute element={<AuthLayout />}>
            <Route path="/home" element={<Home />} />
            <Route
              path=":teamId?/:workspaceId?"
              element={<WorkspaceProtectedRoute />}
            >
              <Route index element={<Workspace />} />
              <Route path="upgrade-needed" element={<UpgradeNeeded />} />
              <Route path="prototypes/:scenarioId" element={<Details />} />
              <Route
                path="prototypes/:scenarioId/simulation/:reviewId"
                element={<SimulationAuth />}
              />
              <Route
                path="prototypes/:scenarioId/human-testing"
                element={<SetUpHumanTesting />}
              />
              <LayoutRoute element={<SimulationLimitProtectedRoute />}>
                <Route path="prototypes/new" element={<SelectFlowAuth />} />

                <Route
                  path="prototypes/new/:nodeId"
                  element={<ConfigureScenarioAuth />}
                />
              </LayoutRoute>
              <LayoutRoute element={<AppraisalLimitProtectedRoute />}>
                <Route path="reviews/new" element={<SelectPageForReview />} />
                <Route
                  path="reviews/new/:nodeId/:method/:level?"
                  element={<NewAuthReview />}
                />
              </LayoutRoute>
              <Route path="reviews/:reviewId" element={<AuthReviewDetails />} />
            </Route>
          </LayoutRoute>
        </LayoutRoute>
        <LayoutRoute
          element={
            <FlagProtectedRoute
              flag="guestEnabled"
              redirectPath={AuthRoutesPaths.Login}
            />
          }
        >
          <LayoutRoute
            element={
              <ProtectedRoute
                redirectPath={AuthRoutesPaths.Login}
                invertRedirectLogic
              />
            }
          >
            <LayoutRoute element={<GuestLayout />}>
              <Route path={AuthRoutesPaths.Guest}>
                <Route index element={<Guest />} />
                <Route path="reviews/new" element={<SelectPageForReview />} />
                <Route
                  path="reviews/new/:nodeId/:method/:level?"
                  element={<NewGuestReview />}
                />
                <Route path="prototypes/new" element={<SelectFlowGuest />} />
                <Route
                  path="prototypes/new/:nodeId"
                  element={<ConfigureScenarioGuest />}
                />
                <Route
                  path=":workspaceId/prototypes/:scenarioId/simulation/:reviewId"
                  element={<SimulationGuest />}
                />
                <Route
                  path=":workspaceId/reviews/:reviewId"
                  element={<GuestReviewDetails />}
                />
              </Route>
            </LayoutRoute>
          </LayoutRoute>
        </LayoutRoute>
        <LayoutRoute element={<RouteWithVersion />}>
          <LayoutRoute
            element={<ProtectedRoute redirectPath="/" invertRedirectLogic />}
          >
            <Route
              path={AuthRoutesPaths.Login}
              element={<AuthenticationPage />}
            />
          </LayoutRoute>
          {UserAuthRoutes}
        </LayoutRoute>
      </Routes>
    </RouterContext.Provider>
  );
};
